import { createSprinklrEngine } from './sprinklrEngine';
import { emitLiveAgentAnalytics } from './analytics';
import { MessageType, createMessage } from '../shared/message';
import { isImageUrlValid } from '../shared/utils/file-upload-utils';
import { logger } from '../shared/utils/logger';
import {
  LIVE_AGENT_ENDED_MESSAGE,
  LIVE_AGENT_IDLE_DISCONNECTED_MESSAGE,
} from '../shared/constants';
import {
  LIVE_AGENT_ANALYTICS_SURVEY_RECEIVED,
  LIVE_AGENT_ANALYTICS_SURVEY_STARTED,
} from './constants';

/**
 * The LiveAgent block: composes the Sprinklr engine with the Conversation handlers and the
 * post-chat survey. A non-hook factory. See .claude/context/live-agent.md
 *
 * @param {Object} ports
 * @param {() => Object} ports.getContext  live consumer context, read at FIRE time:
 *   - `{boolean} enabled`   whether the active journey opts into live agent. The bridge
 *     ratchets membership on; the engine still reads it for its own guards.
 *   - `{Object}  store`     the orchestrator store (escalation, agent flags and
 *     surveyOverlayUrl are published into it).
 *   - `{Object}  conversation`  the Conversation port —
 *     `{ messages, saveMessage, saveMessageIfNew, updateMessage, broadCastMessageUpdate }`.
 *   - `{string}  sessionId`, `{number} idleTTL`  passed through to the engine.
 * @param {Object} ports.persistence  the `maa-live-agent` record port — see
 *   createSprinklrEngine, which is its only user.
 * @returns {Object} `init`/`dispose` plus the capability surface the orchestrator puts on the
 *   flow ctx and the assistant context. It does not expose the engine's conversation ids.
 */
export const createLiveAgent = ({ getContext, persistence }) => {
  const conversation = () => getContext().conversation || {};

  // One mutable object, not named consts — the engine and these handlers reference each other.
  const handlers = {};

  const engine = createSprinklrEngine({
    getContext: () => {
      const {
        enabled, store, sessionId, idleTTL,
      } = getContext();
      return {
        enabled,
        store,
        sessionId,
        idleTTL,
        ...handlers,
        setEscalated: (value) => store?.getState().setIsEscalated(value),
      };
    },
    persistence,
  });

  // ------ Survey overlay: LiveAgent owns the URL state + the analytics; the Shell owns where
  // the overlay renders (a superseding layer).
  const openSurveyOverlay = (url) => {
    const { store, sessionId } = getContext();
    store.getState().setSurveyOverlayUrl(url);
    emitLiveAgentAnalytics({
      lvQuestion: LIVE_AGENT_ANALYTICS_SURVEY_STARTED,
      sessionId,
      ...engine.getConversationIds(),
    });
  };
  const closeSurveyOverlay = () => getContext().store.getState().setSurveyOverlayUrl(null);

  const onSurveyDismiss = (message) => {
    conversation().updateMessage(message, { surveyExpired: true }, true);
  };

  // ------ Sprinklr → Conversation handlers ------
  // No useCallback split needed — a factory has no render for a handler to go stale against.
  handlers.onAgentMessage = async (msg) => {
    await conversation().saveMessageIfNew(msg).catch(() => {});
  };

  // Called by the engine the moment an agent connection is established, by ANY route.
  // Promotion is promote-only and therefore idempotent — the escalation flow also calls it.
  handlers.onAgentConnected = () => {
    const { updateConnectingToConnected, broadCastMessageUpdate } = conversation();
    if (!updateConnectingToConnected) return;
    updateConnectingToConnected()
      .catch(() => {})
      .finally(broadCastMessageUpdate);
  };

  // Called by the engine when Sprinklr signals the conversation has closed.
  handlers.onConversationEnd = (msg) => {
    const { saveMessageIfNew, broadCastMessageUpdate } = conversation();
    saveMessageIfNew({ ...LIVE_AGENT_ENDED_MESSAGE, ...msg })
      .catch(() => {})
      .finally(broadCastMessageUpdate);
  };

  // ⚠ Matches `variant === 'ended'` — the idle variant must stay distinct, or surveys misattach.
  // See .claude/context/live-agent.md
  handlers.onSurveyReceived = (surveyUrl) => {
    const { messages, updateMessage, broadCastMessageUpdate } = conversation();
    const endedMsg = messages.findLast(
      (msg) => msg.type === 'system' && msg.variant === 'ended'
    );
    if (endedMsg) {
      updateMessage(endedMsg, { surveyUrl, surveyAppearedAt: Date.now() }, true)
        .catch(() => {})
        .finally(broadCastMessageUpdate);
      emitLiveAgentAnalytics({
        lvQuestion: LIVE_AGENT_ANALYTICS_SURVEY_RECEIVED,
        sessionId: getContext().sessionId,
        ...engine.getConversationIds(),
      });
    }
  };

  // Called by the engine when an image attachment is received (e.g. user echo after upload).
  handlers.onImageReceived = async ({
    url, title, messageId, isAgent, text = '', timestamp,
  }) => {
    const {
      messages, saveMessageIfNew, updateMessage, broadCastMessageUpdate,
    } = conversation();
    const hasValidImageUrl = await isImageUrlValid(url);
    if (!isAgent) {
      // User echo — patch the existing user message with the hosted URL instead of creating a
      // separate message.
      const userMsg = messages.findLast(
        (msg) => msg.type === MessageType.USER && msg.isImageAttached && !msg.storageLink
      );
      if (userMsg) {
        const imageUpdate = hasValidImageUrl ? { storageLink: url } : { imageLoadFailed: true };
        updateMessage(userMsg, imageUpdate, true)
          .catch(() => {})
          .finally(broadCastMessageUpdate);
      }
      return;
    }

    // ONE agent message with the image embedded — see .claude/context/live-agent.md
    const imageFields = hasValidImageUrl
      ? { isImageAttached: true, storageLink: url, imageFileName: title }
      : { isImageAttached: true, imageLoadFailed: true, imageFileName: title };
    saveMessageIfNew(createMessage(MessageType.AGENT, text, {
      messageId,
      timestamp: timestamp || Date.now(),
      ...imageFields,
    }))
      .then((saved) => {
        logger.log('[liveAgent] Agent image', saved ? 'saved:' : 'already saved by a peer tab:', messageId);
      })
      .catch((err) => logger.error('[liveAgent] Failed to save agent image:', err))
      .finally(broadCastMessageUpdate);
  };

  // Called by the engine when idle TTL has expired. ⚠ The wording is deliberately distinct
  // from every other conversation end — same outcome, different copy, and its own variant.
  handlers.onIdleDisconnect = () => {
    const { saveMessage, broadCastMessageUpdate } = conversation();
    saveMessage(LIVE_AGENT_IDLE_DISCONNECTED_MESSAGE)
      .catch(() => {})
      .finally(broadCastMessageUpdate);
  };

  return {
    init: engine.init,
    dispose: engine.dispose,
    // Capabilities the flows dispatch through
    initiateAgentChat: engine.initiateAgentChat,
    sendAgentMessage: engine.sendAgentMessage,
    sendAgentImage: engine.sendAgentImage,
    updateLastActivity: engine.updateLastActivity,
    handleIdleDisconnect: engine.handleIdleDisconnect,
    checkIdleExpired: engine.checkIdleExpired,
    // Survey lifecycle (exposed on the assistant context for SystemMessage + the Shell)
    openSurveyOverlay,
    closeSurveyOverlay,
    onSurveyDismiss,
  };
};
